import { createColumnHelper } from '@tanstack/react-table';
import { StarIcon } from 'lucide-react';
import { useMemo } from 'react';

import { OrderCurrency } from '@invenco/sales-interface/interfaces';
import type { QuotedShippingRateDTO } from '@invenco/shipping-interface';

import { Card, CardHeader } from '@/components/core/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/core/tooltip';
import { DataTable, useDataTable } from '@/components/data-table';
import { DataTableCardBody, DataTableCardTitle } from '@/components/data-table/data-table-card';
import { formatMoney } from '@/shared/helpers';

export type QuoteRateRow = QuotedShippingRateDTO & {
  rowId: string;
  quoteId: string;
  preferencePlacement: number;
  fromLocationName: string;
  isPreferredRate: boolean;
};

type QuoteRatesCardProps = {
  rates: QuoteRateRow[] | undefined;
  isLoading: boolean;
};

const columnHelper = createColumnHelper<QuoteRateRow>();

function RatePreferenceIndicator({ rate }: { rate: QuoteRateRow }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex min-w-4 items-center justify-center">
          {rate.isPreferredRate ? (
            <StarIcon
              aria-label="Preferred rate"
              role="img"
              className="size-4 fill-primary text-primary"
            />
          ) : (
            <span className="font-medium text-muted-foreground">#{rate.preferencePlacement}</span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>Created quote ID is {rate.quoteId}</TooltipContent>
    </Tooltip>
  );
}

const columns = [
  columnHelper.accessor('isPreferredRate', {
    header: '',
    cell: ({ row }) => <RatePreferenceIndicator rate={row.original} />,
    enableSorting: false,
    meta: { align: 'center' },
  }),
  columnHelper.accessor('fromLocationName', {
    header: 'From location',
    meta: { truncate: true },
  }),
  columnHelper.accessor('carrierName', {
    header: 'Carrier',
    meta: { truncate: true },
  }),
  columnHelper.accessor('carrierServiceName', {
    header: 'Service',
    meta: { truncate: true },
  }),
  columnHelper.accessor('shippingMethodName', {
    header: 'Shipping method',
    meta: { truncate: true },
  }),
  columnHelper.accessor('deliveryDays', {
    header: 'Delivery days',
    meta: { align: 'right' },
  }),
  columnHelper.accessor('baseCost', {
    header: 'Base cost',
    cell: ({ getValue, row }) => {
      const cost = getValue();
      return cost === null ? undefined : formatMoney(cost, row.original.currency as OrderCurrency);
    },
    meta: { align: 'right' },
  }),
  columnHelper.accessor('totalCost', {
    header: 'Total cost',
    cell: ({ getValue, row }) => formatMoney(getValue(), row.original.currency as OrderCurrency),
    meta: { align: 'right' },
  }),
];

export function QuoteRatesCard({ rates, isLoading }: QuoteRatesCardProps) {
  const tableColumns = useMemo(() => columns, []);
  const table = useDataTable({
    data: rates,
    columns: tableColumns,
    getRowId: (row) => row.rowId,
    isLoading,
    // This card has no pagination controls, so opt out of client pagination — otherwise
    // TanStack's default 10-row page size silently hides rows.
    manualPagination: true,
    manualSorting: false,
    loadingRowCount: 3,
  });
  return (
    <Card aria-label="Quoted rates">
      <CardHeader>
        <DataTableCardTitle table={table} rowCount={rates?.length ?? 0}>
          Quoted rates
        </DataTableCardTitle>
      </CardHeader>
      <DataTableCardBody
        table={table}
        emptyMessage={rates ? 'No rates were matched' : 'Create a quote to see rates'}
      >
        <DataTable table={table} />
      </DataTableCardBody>
    </Card>
  );
}
